import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import FontAwesome from "react-fontawesome";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import MarketSelector from "./MarketSelector";
import NavigationDetailContent from "./NavigationDetailContent";
import NavigationToggleButton from "./NavigationToggleButton";

const SECONDARY_RAIL_TRANSITION_MS = 220;

// Primary-rail control used by the collapsed dual-rail navigation shell.
const PrimaryRailItem = memo(
  ({
    item,
    isSelected,
    isActive,
    onActivateLink,
    onToggleRail,
  }) => {
    const className = [
      "sidebar-rail-button",
      isSelected ? "is-selected" : "",
      isActive ? "is-active" : "",
    ]
      .filter(Boolean)
      .join(" ");
    const content = (
      <>
        <span className="sidebar-rail-button__indicator" />
        <span className="sidebar-rail-button__icon" aria-hidden="true">
          <FontAwesome name={item.icon} />
        </span>
        <span className="sidebar-rail-button__label">{item.label}</span>
      </>
    );

    let control = null;

    if (item.kind === "submenu") {
      control = (
        <button
          type="button"
          className={className}
          onClick={() => onToggleRail(item.id)}
          aria-pressed={isSelected}
          aria-expanded={isSelected}
          aria-controls={isSelected ? "sidebar-secondary-rail" : undefined}
          aria-label={item.label}
        >
          {content}
        </button>
      );
    } else if (item.kind === "button") {
      control = (
        <button
          type="button"
          className={className}
          onClick={onActivateLink(item)}
          aria-label={item.label}
        >
          {content}
        </button>
      );
    } else {
      control = (
        <Link
          to={item.to}
          className={className}
          onClick={onActivateLink(item)}
          aria-label={item.label}
        >
          {content}
        </Link>
      );
    }

    return (
      <OverlayTrigger placement="right" overlay={<Tooltip>{item.label}</Tooltip>}>
        {control}
      </OverlayTrigger>
    );
  }
);

// Collapsed navigation shell that owns the dual-rail interaction and secondary detail pane.
const CollapsedSideNav = memo(
  ({
    handleNavLinkClick,
    handleOpenFlexTab,
    onToggleSidebar,
    activeMenuItems,
    isMobile,
    iconLogo,
    topLevelItems,
    utilityItems,
    navigationSearch,
    setNavigationSearch,
    isSearching,
    renderNavigationDescriptor,
    filterNavigationGroups,
    syncAutoExpandedMenuIds,
    onSecondaryRailVisibilityChange,
  }) => {
    const { t } = useTranslation();
    const secondaryRailTimeoutRef = useRef(null);
    const [selectedRailId, setSelectedRailId] = useState(null);
    const [visibleRailId, setVisibleRailId] = useState(null);
    const [isSecondaryRailVisible, setIsSecondaryRailVisible] = useState(false);

    const navigationToggleLabel = t("ToggleNavigationView");

    useEffect(() => {
      setNavigationSearch("");
    }, [selectedRailId, setNavigationSearch]);

    useEffect(() => {
      onSecondaryRailVisibilityChange?.(isSecondaryRailVisible);
    }, [isSecondaryRailVisible, onSecondaryRailVisibilityChange]);

    useEffect(() => {
      return () => {
        if (secondaryRailTimeoutRef.current) {
          clearTimeout(secondaryRailTimeoutRef.current);
        }
      };
    }, []);

    const submenuItems = useMemo(
      () => topLevelItems.filter((item) => item.kind === "submenu"),
      [topLevelItems]
    );

    const isRailItemActive = (item) =>
      (item.id && activeMenuItems?.has(item.id)) ||
      (item.to && activeMenuItems?.has(item.to)) ||
      false;

    useEffect(() => {
      if (submenuItems.length === 0) {
        setSelectedRailId(null);
        return;
      }

      if (
        selectedRailId !== null &&
        !submenuItems.some((item) => item.id === selectedRailId)
      ) {
        setSelectedRailId(null);
      }
    }, [selectedRailId, submenuItems]);

    const visibleRailSection =
      submenuItems.find((item) => item.id === visibleRailId) || null;
    const isVisibleRailRouteActive = visibleRailSection
      ? isRailItemActive(visibleRailSection)
      : false;

    const filteredSecondaryRailState = useMemo(
      () => filterNavigationGroups(visibleRailSection?.groups || []),
      [filterNavigationGroups, visibleRailSection]
    );

    const secondaryRailGroups = useMemo(
      () =>
        filteredSecondaryRailState.groups
          .map((group, index) => ({
            ...group,
            key: `${visibleRailSection?.id || "rail"}-${
              group.label || "default"
            }-${index}`,
            items: (group.items || [])
              .map(renderNavigationDescriptor)
              .filter(Boolean),
          }))
          .filter((group) => group.items.length > 0),
      [filteredSecondaryRailState.groups, renderNavigationDescriptor, visibleRailSection]
    );

    const isMarketSelectorOnlyRail =
      secondaryRailGroups.length === 1 &&
      secondaryRailGroups[0].items.length === 1 &&
      React.isValidElement(secondaryRailGroups[0].items[0]) &&
      secondaryRailGroups[0].items[0].type === MarketSelector;

    useEffect(() => {
      syncAutoExpandedMenuIds(filteredSecondaryRailState.autoExpandedMenuIds);
    }, [
      filteredSecondaryRailState.autoExpandedMenuIds,
      syncAutoExpandedMenuIds,
    ]);

    useEffect(() => {
      if (secondaryRailTimeoutRef.current) {
        clearTimeout(secondaryRailTimeoutRef.current);
        secondaryRailTimeoutRef.current = null;
      }

      if (selectedRailId) {
        setVisibleRailId(selectedRailId);

        requestAnimationFrame(() => {
          setIsSecondaryRailVisible(true);
        });

        return undefined;
      }

      setIsSecondaryRailVisible(false);
      secondaryRailTimeoutRef.current = setTimeout(() => {
        setVisibleRailId(null);
        secondaryRailTimeoutRef.current = null;
      }, SECONDARY_RAIL_TRANSITION_MS);

      return () => {
        if (secondaryRailTimeoutRef.current) {
          clearTimeout(secondaryRailTimeoutRef.current);
          secondaryRailTimeoutRef.current = null;
        }
      };
    }, [selectedRailId]);

    const activatePrimaryLink = (item) => (event) => {
      setSelectedRailId(null);

      if (item.onActivate) {
        item.onActivate(event);
        handleNavLinkClick?.(event);
        return;
      }

      if (!item.component) {
        handleNavLinkClick?.(event);
        return;
      }

      handleOpenFlexTab({
        component: item.component,
        name: item.name,
        id: item.id,
        ...item.config,
      });
      handleNavLinkClick?.(event);
    };

    const toggleSecondaryRail = (itemId) => {
      setSelectedRailId((currentRailId) =>
        currentRailId === itemId ? null : itemId
      );
    };

    return (
      <div className="sidebar-dual-rail">
        <div className="sidebar-primary-rail">
          <div className="sidebar-primary-rail__header">
            {!isMobile && (
              <div className="sidebar-brand">
                <Link to="/" aria-label="Neutral Markets home">
                  <img
                    src={iconLogo}
                    alt="Neutral Markets"
                    className="icon-logo"
                  />
                </Link>
              </div>
            )}
            <NavigationToggleButton
              icon="bars"
              label={navigationToggleLabel}
              onClick={onToggleSidebar}
            />
          </div>

          <div className="sidebar-primary-rail__menu">
            {topLevelItems.map((item) => (
              <PrimaryRailItem
                key={item.id}
                item={item}
                isSelected={selectedRailId === item.id}
                isActive={isRailItemActive(item)}
                onActivateLink={activatePrimaryLink}
                onToggleRail={toggleSecondaryRail}
              />
            ))}
          </div>

          <div className="sidebar-primary-rail__footer">
            {utilityItems.map((item) => (
              <PrimaryRailItem
                key={item.id}
                item={item}
                isSelected={selectedRailId === item.id}
                isActive={isRailItemActive(item)}
                onActivateLink={activatePrimaryLink}
                onToggleRail={toggleSecondaryRail}
              />
            ))}
          </div>
        </div>

        {visibleRailSection && (
          <div
            className={`sidebar-secondary-rail sidebar-detail-pane${
              isSecondaryRailVisible ? " is-open" : ""
            }`}
            id="sidebar-secondary-rail"
            aria-label={`${visibleRailSection.label} navigation`}
            aria-hidden={!isSecondaryRailVisible}
          >
            <div className="sidebar-secondary-rail__header">
              {visibleRailSection.sectionLabel ? (
                <div className="sidebar-secondary-rail__eyebrow">
                  {visibleRailSection.sectionLabel}
                </div>
              ) : null}
              <div className="sidebar-secondary-rail__title-row">
                <span
                  className={[
                    "sidebar-secondary-rail__icon",
                    isVisibleRailRouteActive ? "is-active" : "is-selected",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  aria-hidden="true"
                >
                  <FontAwesome name={visibleRailSection.icon} />
                </span>
                <h2 className="sidebar-secondary-rail__title">
                  {visibleRailSection.label}
                </h2>
                <OverlayTrigger
                  placement="right"
                  overlay={<Tooltip>{t("CollapseNavigationRail")}</Tooltip>}
                >
                  <button
                    type="button"
                    className="sidebar-secondary-rail__collapse-button"
                    onClick={() => toggleSecondaryRail(visibleRailSection.id)}
                    aria-label={t("CollapseNavigationRail")}
                  >
                    <FontAwesome name="chevron-left" />
                  </button>
                </OverlayTrigger>
              </div>
            </div>

            <NavigationDetailContent
              className="sidebar-secondary-rail__content"
              searchLabel={t("NavigationSearch")}
              searchValue={navigationSearch}
              onSearchChange={setNavigationSearch}
              showSearch={!isMarketSelectorOnlyRail}
              groups={secondaryRailGroups}
              isSearching={isSearching}
              emptyStateLabel={t("NoMatchingNavigationItems")}
            />
          </div>
        )}
      </div>
    );
  }
);

export default CollapsedSideNav;


